if (!customElements.get('collection-roof-filter')) {
  class CollectionRoofFilter extends HTMLElement {
    static SELECTORS = {
      filterContainer: '[js-roof-filter-item-container]',
      outputContainer: '[js-roof-filter-item-output-container]',
      optionsContainer: '[js-roof-filter-item-options-container] ul',
      optionsContainerWrapper: '[js-roof-filter-item-options-container]',
      input: '[js-input]',
      label: 'label',
      defaultLabel: '[js-default-label]',
      resetButton: '[js-reset-button]',
      resultContainer: '[js-roof-filter-result]',
      submitButton: '[js-pdp-filter-button]',
      totalCount: '[js-total-count]',
      productRoofLimit: '[js-product-roofload-limit]',
      eligibleIndicator: '[js-roofl-load-eligible]',
      ineligibleIndicator: '[js-roofl-load-inegible]',
      defaultText: '[js-roofl-load-default-text]',
      popup: '[js-filter-popup]',
      popupButton: '[js-filter-popup-button]'
    };

    static LOGO_BASE_URL = 'https://www.auto-data.net/img/logos';
    static DATA_CHUNK_URLS = [
      'https://cdn.shopify.com/s/files/1/0581/2797/4597/files/roofloadData_chunk1.json?v=1774947106',
      'https://cdn.shopify.com/s/files/1/0581/2797/4597/files/roofloadData_chunk2.json?v=1774947115',
    ];
    static CACHE_KEY = 'roofFilterData';
    static CACHE_TTL = 5 * 24 * 60 * 60 * 1000;
    static DB_NAME = 'RoofFilterCache';
    static DB_VERSION = 1;
    static STORE_NAME = 'cache';

    constructor() {
      super();
      this.initializeState();
      this.cacheElements();
    }

    initializeState() {
      this.brandsData = null;
      this.selectedBrandModels = null;
      this.selectedModelGenerations = null;
      this.selectedGeneration = null;
      this.currentRoofLoad = null;
    }

    cacheElements() {
      this.brandFilter = this.querySelector('[data-type="brands"]');
      this.modelFilter = this.querySelector('[data-type="models"]');
      this.generationFilter = this.querySelector('[data-type="generation"]');

      this.brandOptions = this.brandFilter?.querySelector(CollectionRoofFilter.SELECTORS.optionsContainer);
      this.modelOptions = this.modelFilter?.querySelector(CollectionRoofFilter.SELECTORS.optionsContainer);
      this.generationOptions = this.generationFilter?.querySelector(CollectionRoofFilter.SELECTORS.optionsContainer);

      this.resultDisplay = this.querySelector(CollectionRoofFilter.SELECTORS.resultContainer);
      this.submitButton = this.querySelector(CollectionRoofFilter.SELECTORS.submitButton);
    }

    async connectedCallback() {
      this.bindEvents();
      const rawData = await this.fetchFilterData();
      this.filterData = this.filterByValidRoofLoad(rawData);
      this.renderBrandOptions(this.filterData?.brands);
      this.bindBrandSelection();
    }

    bindEvents() {
      this.bindDropdownToggle();
      this.bindOutsideClick();
      this.bindResetButton();
      this.bindSubmitButton();
    }

    bindDropdownToggle() {
      this.querySelectorAll(CollectionRoofFilter.SELECTORS.outputContainer).forEach(output => {
        output.addEventListener('click', () => {
          const container = output.closest(CollectionRoofFilter.SELECTORS.filterContainer);
          const isOpen = container.classList.contains('open');

          this.closeAllDropdowns();
          if (!isOpen) container.classList.add('open');
        });
      });
    }

    bindOutsideClick() {
      document.addEventListener('click', (event) => {
        this.querySelectorAll(CollectionRoofFilter.SELECTORS.filterContainer).forEach(container => {
          if (!container.contains(event.target)) {
            container.classList.remove('open');
          }
        });
      });
    }

    bindResetButton() {
      this.querySelector(CollectionRoofFilter.SELECTORS.resetButton)?.addEventListener('click', () => {
        this.currentRoofLoad = null;
        this.updateRoofLoadDisplay(null);
        this.resetFilterOutputs(this.brandFilter, true);
        this.querySelectorAll('[js-input]:checked')?.forEach(checkedInput => checkedInput.checked = false)
      });
    }

    bindSubmitButton() {
      this.submitButton?.addEventListener('click', () => {
        document.querySelector(CollectionRoofFilter.SELECTORS.popup)?.classList.remove('active');
      });
    }

    closeAllDropdowns() {
      this.querySelectorAll(CollectionRoofFilter.SELECTORS.filterContainer).forEach(container => {
        container.classList.remove('open');
      });
    }

    static openDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(CollectionRoofFilter.DB_NAME, CollectionRoofFilter.DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(CollectionRoofFilter.STORE_NAME)) {
            db.createObjectStore(CollectionRoofFilter.STORE_NAME);
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }

    static async clearCache() {
      try {
        const db = await CollectionRoofFilter.openDB();
        const tx = db.transaction(CollectionRoofFilter.STORE_NAME, 'readwrite');
        tx.objectStore(CollectionRoofFilter.STORE_NAME).delete(CollectionRoofFilter.CACHE_KEY);
        await new Promise((resolve, reject) => { tx.oncomplete = resolve; tx.onerror = () => reject(tx.error); });
        db.close();
      } catch (e) { /* ignore */ }
      // Clean up old localStorage chunks from previous implementation
      try {
        for (let i = 0; i < 100; i++) {
          const key = `${CollectionRoofFilter.CACHE_KEY}_chunk_${i}`;
          if (localStorage.getItem(key) === null) break;
          localStorage.removeItem(key);
        }
        localStorage.removeItem(`${CollectionRoofFilter.CACHE_KEY}_meta`);
        localStorage.removeItem(CollectionRoofFilter.CACHE_KEY);
      } catch (e) { /* ignore */ }
    }

    static async cacheData(data) {
      try {
        await CollectionRoofFilter.clearCache();
        const db = await CollectionRoofFilter.openDB();
        const tx = db.transaction(CollectionRoofFilter.STORE_NAME, 'readwrite');
        tx.objectStore(CollectionRoofFilter.STORE_NAME).put({ data, timestamp: Date.now() }, CollectionRoofFilter.CACHE_KEY);
        await new Promise((resolve, reject) => { tx.oncomplete = resolve; tx.onerror = () => reject(tx.error); });
        db.close();
      } catch (e) { /* non-fatal, data is already in memory */ }
    }

    static async getCachedData() {
      try {
        const db = await CollectionRoofFilter.openDB();
        const tx = db.transaction(CollectionRoofFilter.STORE_NAME, 'readonly');
        const request = tx.objectStore(CollectionRoofFilter.STORE_NAME).get(CollectionRoofFilter.CACHE_KEY);
        const record = await new Promise((resolve, reject) => { request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); });
        db.close();
        if (!record) return null;
        if (Date.now() - record.timestamp >= CollectionRoofFilter.CACHE_TTL) return null;
        return record.data;
      } catch (e) { return null; }
    }

    async fetchFilterData() {
      // const cached = await CollectionRoofFilter.getCachedData();
      // if (cached) return cached;

      const responses = await Promise.all(
        CollectionRoofFilter.DATA_CHUNK_URLS.map(url => fetch(url))
      );
      const chunks = await Promise.all(
        responses.map(res => res.json())
      );

      const mergedBrands = chunks.flatMap(chunk => chunk?.brands?.brand ?? []);
      const data = { brands: { brand: mergedBrands } };

      console.log({ data })

      await CollectionRoofFilter.cacheData(data);
      return data;
    }

    filterByValidRoofLoad(data) {
      const brands = data?.brands?.brand ?? [];

      const hasValidRoofLoad = (modification) =>
        modification &&
        Object.prototype.hasOwnProperty.call(modification, 'roofLoad') &&
        String(modification.roofLoad).trim().length > 0;

      const filteredBrands = brands
        .map(brand => this.filterBrandModels(brand, hasValidRoofLoad))
        .filter(Boolean);

      return { brands: { brand: filteredBrands } };
    }

    filterBrandModels(brand, roofLoadValidator) {
      const models = brand?.models?.model ?? [];

      const filteredModels = models
        .map(model => this.filterModelGenerations(model, roofLoadValidator))
        .filter(Boolean);

      if (filteredModels.length === 0) return null;

      return { ...brand, models: { ...brand.models, model: filteredModels } };
    }

    filterModelGenerations(model, roofLoadValidator) {
      const generations = model?.generations?.generation ?? [];

      const filteredGenerations = generations
        .map(generation => this.filterGenerationModifications(generation, roofLoadValidator))
        .filter(Boolean);

      if (filteredGenerations.length === 0) return null;

      return { ...model, generations: { ...model.generations, generation: filteredGenerations } };
    }

    filterGenerationModifications(generation, roofLoadValidator) {
      const modifications = generation?.modifications?.modification ?? [];
      const validModifications = modifications.filter(roofLoadValidator);

      if (validModifications.length === 0) return null;

      return { ...generation, modifications: { ...generation.modifications, modification: validModifications } };
    }

    bindBrandSelection() {
      const outputContainer = this.brandFilter?.querySelector(CollectionRoofFilter.SELECTORS.outputContainer);

      this.brandOptions?.querySelectorAll(CollectionRoofFilter.SELECTORS.input).forEach(input => {
        input.addEventListener('change', (event) => {
          const brandName = event.target.value;
          this.brandOptions.closest(CollectionRoofFilter.SELECTORS.filterContainer).classList.remove('open');
          this.updateBrandOutput(outputContainer, brandName);
          this.renderModelOptions(brandName);
          this.bindModelSelection();
          this.resetFilterOutputs(this.brandFilter);
        });
      });
    }

    updateBrandOutput(container, brandName) {
      const label = container?.querySelector(CollectionRoofFilter.SELECTORS.label);
      if (!label) return;

      label.innerHTML = this.createBrandLabelHTML(brandName);
    }

    bindModelSelection() {
      const outputContainer = this.modelFilter?.querySelector(CollectionRoofFilter.SELECTORS.outputContainer);

      this.modelOptions?.querySelectorAll(CollectionRoofFilter.SELECTORS.input).forEach(input => {
        input.addEventListener('change', (event) => {
          const modelName = event.target.value;
          this.modelOptions.closest(CollectionRoofFilter.SELECTORS.filterContainer).classList.remove('open');
          this.updateTextOutput(outputContainer, modelName);
          this.renderGenerationOptions(modelName);
          this.bindGenerationSelection();
          this.resetFilterOutputs(this.modelFilter);
        });
      });
    }

    bindGenerationSelection() {
      const outputContainer = this.generationFilter?.querySelector(CollectionRoofFilter.SELECTORS.outputContainer);

      this.generationOptions?.querySelectorAll(CollectionRoofFilter.SELECTORS.input).forEach(input => {
        input.addEventListener('change', (event) => {
          const generationLabel = event.target.value;
          this.generationOptions.closest(CollectionRoofFilter.SELECTORS.filterContainer).classList.remove('open');
          this.updateTextOutput(outputContainer, generationLabel);
          this.resetFilterOutputs(this.generationFilter);
          this.processRoofLoadSelection(generationLabel);
        });
      });
    }

    updateTextOutput(container, text) {
      const label = container?.querySelector(CollectionRoofFilter.SELECTORS.label);
      if (label) label.innerText = text;
    }

    processRoofLoadSelection(generationLabel) {
      const selectedGeneration = this.selectedModelGenerations?.find(
        gen => `${gen.modelYear} - ${gen.name}` === generationLabel
      );

      this.selectedGeneration = selectedGeneration;
      this.currentRoofLoad = null;

      selectedGeneration?.modifications?.modification.forEach(mod => {
        if (mod?.roofLoad) this.currentRoofLoad = mod.roofLoad;
      });

      this.updateRoofLoadDisplay(this.currentRoofLoad);
    }

    updateRoofLoadDisplay(roofLoad) {
      const productElements = document.querySelectorAll(CollectionRoofFilter.SELECTORS.productRoofLimit);
      const roofLoadValue = roofLoad ? parseInt(roofLoad) : null;

      productElements?.forEach(element => {
        if (!roofLoadValue) {
          element.classList.add('hidden');
          return;
        }

        element.classList.remove('hidden');
        const productLimit = parseInt(element.getAttribute('data-product-roofload-limit'));
        const isEligible = productLimit <= roofLoadValue;

        this.toggleEligibilityIndicators(element, isEligible);
      });

      this.updateEligibleCount(roofLoadValue, productElements);
      this.updatePopupButton(roofLoad);
    }

    toggleEligibilityIndicators(element, isEligible) {
      const eligible = element.querySelector(CollectionRoofFilter.SELECTORS.eligibleIndicator);
      const ineligible = element.querySelector(CollectionRoofFilter.SELECTORS.ineligibleIndicator);

      eligible?.classList.toggle('hidden', !isEligible);
      ineligible?.classList.toggle('hidden', isEligible);
    }

    updateEligibleCount(roofLoadValue, productElements) {
      let eligibleCount = 0;

      if (roofLoadValue) {
        productElements?.forEach(element => {
          const productLimit = parseInt(element.getAttribute('data-product-roofload-limit'));
          if (productLimit <= roofLoadValue) eligibleCount++;
        });
      }

      if (this.resultDisplay) {
        this.resultDisplay.classList.toggle('hidden', !roofLoadValue);
        this.resultDisplay.querySelector(CollectionRoofFilter.SELECTORS.totalCount).innerText = eligibleCount;
      }

      this.submitButton?.classList.toggle('active', !!roofLoadValue);
    }

    updatePopupButton(roofLoad) {
      const popupButton = document.querySelector(CollectionRoofFilter.SELECTORS.popupButton);
      if (!popupButton) return;

      const eligible = popupButton.querySelector(CollectionRoofFilter.SELECTORS.eligibleIndicator);
      const ineligible = popupButton.querySelector(CollectionRoofFilter.SELECTORS.ineligibleIndicator);
      const defaultText = popupButton.querySelector(CollectionRoofFilter.SELECTORS.defaultText);

      if (!roofLoad) {
        eligible?.classList.add('hidden');
        ineligible?.classList.add('hidden');
        defaultText?.classList.remove('hidden');
        popupButton.classList.remove('eligible', 'inegible');
        return;
      }

      const productLimit = parseInt(popupButton.getAttribute('data-product-roofload-limit'));
      const roofLoadValue = parseInt(roofLoad);
      const isEligible = productLimit <= roofLoadValue;

      defaultText?.classList.add('hidden');
      eligible?.classList.toggle('hidden', !isEligible);
      ineligible?.classList.toggle('hidden', isEligible);
      popupButton.classList.toggle('eligible', isEligible);
      popupButton.classList.toggle('inegible', !isEligible);
    }

    renderBrandOptions(brands) {
      this.brandsData = brands?.brand;
      this.brandOptions.innerHTML = '';

      this.brandsData?.forEach(brand => {
        this.renderFilterOption(brand.name, this.brandOptions, 'brand');
      });

      this.brandFilter?.classList.add('fill');
      this.currentRoofLoad = null;
      this.updateRoofLoadDisplay(null);
    }

    renderModelOptions(brandName) {
      const selectedBrand = this.brandsData?.find(brand => brand.name === brandName);
      this.selectedBrandModels = selectedBrand?.models?.model;
      this.modelOptions.innerHTML = '';

      this.selectedBrandModels?.forEach(model => {
        this.renderFilterOption(model.name, this.modelOptions, 'model');
      });

      this.modelFilter?.classList.add('fill');
      this.currentRoofLoad = null;
      this.updateRoofLoadDisplay(null);
      this.scrollBarInit(this.modelOptions);
    }

    renderGenerationOptions(modelName) {
      const selectedModel = this.selectedBrandModels?.find(model => model.name === modelName);
      this.selectedModelGenerations = selectedModel?.generations?.generation;
      this.generationOptions.innerHTML = '';

      this.selectedModelGenerations?.forEach(generation => {
        const label = `${generation.modelYear} - ${generation.name}`;
        this.renderFilterOption(label, this.generationOptions, 'generation');
      });

      this.generationFilter?.classList.add('fill');
      this.currentRoofLoad = null;
      this.updateRoofLoadDisplay(null);
      this.scrollBarInit(this.modelOptions);
    }

    renderFilterOption(name, container, type) {
      const handle = name.replace(/\s+/g, '_');
      const logoHTML = type === 'brand'
        ? `<div class="roofload-filter__option--logo">
            <img src="${CollectionRoofFilter.LOGO_BASE_URL}/${handle}.png" alt="${name}">
           </div>`
        : '';

      const optionHTML = `
        <label for="filter-${type}-${handle}" class="roofload-filter__option swiper-slide">
          <div class="roofload-filter__option--wrapper">
            ${logoHTML}
            <span js-label class="roofload-filter__option--name">${name}</span>
            <input js-input id="filter-${type}-${handle}" name="${type}" type="radio" value="${name}" class="roofload-filter__option--radio hidden">
          </div>
          <span class="roofload-filter__option--icon">${this.getCheckmarkSVG(handle)}</span>
        </label>
      `;

      container.insertAdjacentHTML('beforeend', optionHTML);
    }

    createBrandLabelHTML(brandName) {
      const handle = brandName.replace(/\s+/g, '_');
      return `
        <div class="roofload-filter__option--wrapper">
          <div class="roofload-filter__option--logo">
            <img src="${CollectionRoofFilter.LOGO_BASE_URL}/${handle}.png" alt="${brandName}">
          </div>
          <span js-label class="roofload-filter__option--name">${brandName}</span>
        </div>
      `;
    }

    getCheckmarkSVG(uniqueId) {
      return `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip-${uniqueId})">
            <path d="M3.125 11.25L7.5 15.625L17.5 5.625" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </g>
          <defs>
            <clipPath id="clip-${uniqueId}">
              <rect width="20" height="20" fill="white"/>
            </clipPath>
          </defs>
        </svg>
      `;
    }

    resetFilterOutputs(container, resetAll = false) {
      let sibling = container?.nextElementSibling;

      while (sibling) {
        this.showDefaultLabel(sibling);
        sibling = sibling.nextElementSibling;
      }

      if (resetAll) {
        this.showDefaultLabel(container);
      } else {
        this.showSelectedLabel(container);
      }
    }

    showDefaultLabel(element) {
      element?.querySelector(`${CollectionRoofFilter.SELECTORS.outputContainer} ${CollectionRoofFilter.SELECTORS.defaultLabel}`)?.classList.remove('hidden');
      element?.querySelector(`${CollectionRoofFilter.SELECTORS.outputContainer} ${CollectionRoofFilter.SELECTORS.label}`)?.classList.add('hidden');
    }

    showSelectedLabel(element) {
      element?.querySelector(`${CollectionRoofFilter.SELECTORS.outputContainer} ${CollectionRoofFilter.SELECTORS.defaultLabel}`)?.classList.add('hidden');
      element?.querySelector(`${CollectionRoofFilter.SELECTORS.outputContainer} ${CollectionRoofFilter.SELECTORS.label}`)?.classList.remove('hidden');
    }

    scrollBarInit(container) {
      const slideContainer = container.closest(CollectionRoofFilter.SELECTORS.optionsContainerWrapper);
      slideContainer.swiper.init();
    }
  }

  customElements.define('collection-roof-filter', CollectionRoofFilter);
}

document.addEventListener('DOMContentLoaded', () => {
  const popupButton = document.querySelector('[js-filter-popup-button]');
  const popup = document.querySelector('[js-filter-popup]');
  const overlay = document.querySelector('[js-filter-popup-overlay]');
  const closeButton = document.querySelector('[js-filter-popup-close-button]');

  if (!popupButton || !popup) return;

  popupButton.addEventListener('click', () => popup.classList.add('active'));

  [overlay, closeButton].forEach(element => {
    element?.addEventListener('click', () => popup.classList.remove('active'));
  });
});

document.addEventListener("DOMContentLoaded", () => {
  const filterSliderMain = document.querySelectorAll("[js-roof-filter-item-container]");
  if (!filterSliderMain.length) return;
  if (typeof Swiper === "undefined") {
    console.warn("Swiper is not loaded on the page.");
    return;
  }

  filterSliderMain.forEach((mainSlider) => {
    const scrollSlider = mainSlider.querySelector("[js-roof-filter-item-options-container]");
    if (!scrollSlider) return;
      const scrollbarEl = scrollSlider.querySelector(".swiper-scrollbar");
      new Swiper(scrollSlider, {
        direction: "vertical",
        slidesPerView: "auto",
        freeMode: true,
        mousewheel: true,
        scrollbar: scrollbarEl
          ? {
              el: scrollbarEl,
              draggable: true,
            }
          : undefined,
      });
  });
});
